import { useState, useMemo } from "react";
import { UserPlus, Share2, MessageCircle, Mail, Phone, Send, Copy, Contact, Check, Users } from "lucide-react";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { DictationButton } from "@/components/DictationButton";
import { useAuth } from "@/context/AuthContext";

const SUBJECT = "Découvrez Path-Intelligent — pathologie numérique";

export default function InviteDialog() {
  const { user } = useAuth();
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const refUrl = user?.id ? `${origin}/?ref=${user.id}` : origin;
  const [msg, setMsg] = useState(
    "Bonjour,\n\nJe vous invite à découvrir Path-Intelligent, une plateforme québécoise de pathologie numérique : "
    + "lames virtuelles haute résolution, aide au diagnostic par IA et conformité à la Loi 25.\n\n"
    + `Découvrez-la ici : ${refUrl}`,
  );
  const [copied, setCopied] = useState(false);
  const [picked, setPicked] = useState(null);
  const [groupOn, setGroupOn] = useState(false);
  const [recipients, setRecipients] = useState("");

  const enc = encodeURIComponent;
  const supportsShare = typeof navigator !== "undefined" && !!navigator.share;
  const supportsContacts = typeof navigator !== "undefined" && "contacts" in navigator
    && navigator.contacts && "select" in navigator.contacts;

  const links = useMemo(() => ({
    whatsapp: `https://wa.me/?text=${enc(msg)}`,
    telegram: `https://t.me/share/url?url=${enc(refUrl)}&text=${enc(msg)}`,
    email: `mailto:?subject=${enc(SUBJECT)}&body=${enc(msg)}`,
    sms: `sms:?&body=${enc(msg)}`,
  }), [msg, refUrl]);

  const nativeShare = async () => {
    try { await navigator.share({ title: "Path-Intelligent", text: msg, url: refUrl }); } catch { /* annulé */ }
  };
  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(msg);
      setCopied(true); setTimeout(() => setCopied(false), 2000);
      toast.success("Invitation copiée");
    } catch { toast.error("Impossible de copier"); }
  };
  const openMessenger = async () => {
    try { await navigator.clipboard.writeText(msg); } catch { /* noop */ }
    toast.info("Invitation copiée — collez-la dans Messenger");
    window.open("https://www.messenger.com/", "_blank", "noopener");
  };

  const parseList = () => recipients.split(/[\s,;]+/).map((x) => x.trim()).filter(Boolean);
  const sendGroupEmail = () => {
    const list = parseList().filter((x) => x.includes("@"));
    if (!list.length) { toast.error("Ajoutez au moins un courriel"); return; }
    window.location.href = `mailto:${list.join(",")}?subject=${enc(SUBJECT)}&body=${enc(msg)}`;
  };
  const sendGroupSms = () => {
    const list = parseList().filter((x) => /[0-9]/.test(x));
    if (!list.length) { toast.error("Ajoutez au moins un numéro"); return; }
    window.location.href = `sms:${list.join(",")}?&body=${enc(msg)}`;
  };

  const pickContacts = async () => {
    try {
      const available = await navigator.contacts.getProperties();
      const want = ["name", "email", "tel"].filter((p) => available.includes(p));
      const contacts = await navigator.contacts.select(want, { multiple: true });
      if (!contacts || contacts.length === 0) return;
      const emails = contacts.flatMap((c) => c.email || []);
      const tels = contacts.flatMap((c) => c.tel || []);
      setPicked({ count: contacts.length, emails: emails.length, tels: tels.length });
      setGroupOn(true);
      setRecipients([...emails, ...tels].join(", "));
    } catch { toast.error("Sélection de contacts annulée ou non autorisée"); }
  };

  const ChanBtn = ({ href, onClick, icon: Icon, label, testid }) => {
    const cls = "flex items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50 hover:border-sky-300 transition-colors no-underline";
    const inner = <><Icon className="h-4 w-4" /> {label}</>;
    return href
      ? <a href={href} target="_blank" rel="noopener noreferrer" className={cls} data-testid={testid}>{inner}</a>
      : <button type="button" onClick={onClick} className={cls} data-testid={testid}>{inner}</button>;
  };

  return (
    <Dialog>
      <DialogTrigger asChild>
        <button data-testid="invite-open-button"
          className="mx-3 mb-2 flex w-[calc(100%-1.5rem)] items-center gap-3 px-3 py-2.5 rounded-md text-sm text-amber-200 hover:bg-amber-400/10 transition-colors border border-amber-300/30">
          <UserPlus className="h-4 w-4" />
          <span className="text-left leading-tight">Inviter mes collègues</span>
        </button>
      </DialogTrigger>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="invite-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="h-5 w-5 text-sky-600" />
            Inviter mes collègues pathologistes et médecins
          </DialogTitle>
          <DialogDescription>
            Partagez Path-Intelligent par téléphone, courriel, Messenger, WhatsApp… Vos contacts ne sont jamais envoyés à nos serveurs.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <label className="text-xs font-medium text-slate-500">Message d'invitation</label>
            <Textarea data-testid="invite-message" value={msg} onChange={(e) => setMsg(e.target.value)} rows={5} className="mt-1 text-sm" />
            <DictationButton value={msg} onChange={setMsg} testId="dictation-invite-message" />
          </div>

          {supportsShare && (
            <Button onClick={nativeShare} className="w-full" data-testid="invite-native-share">
              <Share2 className="h-4 w-4 mr-2" /> Partager…
            </Button>
          )}

          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
            <ChanBtn href={links.whatsapp} icon={MessageCircle} label="WhatsApp" testid="invite-whatsapp" />
            <ChanBtn href={links.email} icon={Mail} label="Courriel" testid="invite-email" />
            <ChanBtn href={links.sms} icon={Phone} label="SMS" testid="invite-sms" />
            <ChanBtn href={links.telegram} icon={Send} label="Telegram" testid="invite-telegram" />
            <ChanBtn onClick={openMessenger} icon={MessageCircle} label="Messenger" testid="invite-messenger" />
            <ChanBtn onClick={copyLink} icon={copied ? Check : Copy} label={copied ? "Copié !" : "Copier"} testid="invite-copy" />
          </div>

          <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
            <input type="checkbox" checked={groupOn} onChange={(e) => setGroupOn(e.target.checked)} data-testid="invite-group-toggle" className="h-4 w-4 accent-sky-600" />
            <Users className="h-4 w-4 text-slate-500" /> Envoyer à plusieurs contacts à la fois
          </label>
          {groupOn && (
            <div className="space-y-2 rounded-lg border border-slate-200 p-3" data-testid="invite-group-panel">
              <Textarea value={recipients} onChange={(e) => setRecipients(e.target.value)} rows={2}
                placeholder="Courriels ou numéros séparés par des virgules" className="text-sm" data-testid="invite-group-recipients" />
              <div className="grid grid-cols-2 gap-2">
                <Button variant="secondary" onClick={sendGroupEmail} data-testid="invite-group-email"><Mail className="h-4 w-4 mr-1" />Courriel groupé</Button>
                <Button variant="secondary" onClick={sendGroupSms} data-testid="invite-group-sms"><Phone className="h-4 w-4 mr-1" />SMS groupé</Button>
              </div>
              {picked && <p className="text-xs text-slate-500" data-testid="invite-picked-info">{picked.count} contact(s) · {picked.emails} courriel(s) · {picked.tels} numéro(s)</p>}
            </div>
          )}

          {supportsContacts && (
            <Button variant="outline" onClick={pickContacts} className="w-full" data-testid="invite-pick-contacts">
              <Contact className="h-4 w-4 mr-2" /> Choisir dans mes contacts
            </Button>
          )}

          <div className="flex items-center gap-4 rounded-lg border border-slate-200 p-3">
            <div className="bg-white p-1.5 rounded-md border shrink-0"><QRCodeSVG value={refUrl} size={92} data-testid="invite-qr" /></div>
            <div className="text-xs text-slate-500">
              <div className="font-semibold text-slate-700 mb-0.5">QR de parrainage</div>
              À afficher ou imprimer pour vos événements — il ouvre l'application avec votre lien personnel.
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
